<template>
  <div class="vscode-editor">
    <!-- Top Menu Bar -->
    <div class="vscode-menubar">
      <div class="vscode-menubar-left">
        <slot name="nav"></slot>
      </div>
      <div class="vscode-menubar-center">
        <span class="vscode-title">{{ $route.query.v ? `Version ${$route.query.v}` : 'Template Editor' }}</span>
      </div>
      <div class="vscode-menubar-right">
        <b-icon 
          icon="chat-left-text" 
          class="vscode-chat-icon"
          @click="toggleChat"
          v-b-tooltip.hover 
          title="Open AI Chat"
        ></b-icon>
      </div>
    </div>

    <!-- Builder Settings Modal -->
    <main-dialog
      v-model="localShowBuilderSettings"
      title="Builder Settings"
      header-bg-variant="dark"
      body-bg-variant="dark"
      footer-bg-variant="dark"
      @ok="$emit('connectToBuilder')"
    >
      <b-form-group label="Builder Port" label-for="builder-port">
        <b-form-input
          id="builder-port"
          v-model="localBuilderPort"
          type="number"
          placeholder="11403"
        ></b-form-input>
      </b-form-group>
      <div class="mt-2">
        <small class="text-muted">
          Current status: 
          <span :class="builderConnected ? 'text-success' : 'text-danger'">
            {{ builderConnected ? `Connected to port ${builderPort}` : 'Disconnected' }}
          </span>
        </small>
      </div>
    </main-dialog>

    <div class="vscode-container">
      <!-- Sidebar -->
      <vue-resizable :width="sidebarWidth" :active="handlers" :maxWidth="500" :minWidth="200"
        @resize:move="onSidebarResize">
        <div class="vscode-sidebar">
          <div class="vscode-sidebar-header">
            <span class="vscode-sidebar-title">EXPLORER</span>
            <div class="vscode-sidebar-actions">
              <b-icon icon="file-earmark-plus" @click="addFileToRoot" v-b-tooltip.hover title="New File" class="vscode-sidebar-icon"></b-icon>
              <b-icon icon="folder-plus" @click="addFolderToRoot" v-b-tooltip.hover title="New Folder" class="vscode-sidebar-icon"></b-icon>
            </div>
          </div>
          <div class="vscode-sidebar-content" v-if="nodes && nodes.children">
            <div class="vscode-tree">
              <tree-node
                v-for="node in nodes.children"
                :key="node.id"
                :node="node"
                :level="0"
                :editing-id="nodeEditable"
                @click="onNodeClick"
                @toggle="onNodeToggle"
                @contextmenu="openMenu"
                @rename="handleRename"
              />
            </div>

            <ul id="vscode-context-menu" tabindex="-1" ref="right" v-if="viewMenu" @blur="closeMenu"
              :style="{ top: top, left: left }">
              <li v-for="nodeAction in nodeActions()" @click="triggerNodeAction(nodeAction)" :key="nodeAction">
                {{ nodeAction }}
              </li>
            </ul>
          </div>
        </div>
      </vue-resizable>

      <!-- Editor Area -->
      <div class="vscode-editor-area">
        <!-- Left/Main Editor -->
        <vue-resizable 
          v-if="activeEditorsRight.length" 
          :width="leftEditorWidth" 
          :active="handlers"
          @resize:move="onEditorResize"
          class="vscode-editor-pane"
        >
          <div class="vscode-editor-container">
            <div class="vscode-tabs">
              <div 
                v-for="(activeEditor, index) in activeEditors" 
                :key="activeEditor.id"
                :class="['vscode-tab', { 'vscode-tab-active': activeTab === index }]"
                @click="activeTab = index"
              >
                <img :src="iconRender(activeEditor.ext)" class="vscode-tab-icon" />
                <span class="vscode-tab-label">{{ activeEditor.name }}</span>
                <span class="vscode-tab-close" @click.stop="closeActiveEditor(activeEditor)">
                  <b-icon icon="x" font-scale="0.9"></b-icon>
                </span>
              </div>
            </div>
            <div class="vscode-editor-content">
              <div v-if="activeEditors.length > 0" class="vscode-editor-file">
                <div class="vscode-breadcrumb">
                  {{ generateNodePath(activeEditors[activeTab], activeEditors[activeTab].name) }}
                </div>
                <MonacoEditor 
                  :value="currentEditorValue(activeEditors[activeTab])"
                  @input="(e) => editorInput(activeEditors[activeTab], e)"
                  :language="getLanguageFromFile(activeEditors[activeTab].name)"
                  :options="monacoOptions"
                  class="vscode-monaco"
                  :showFullscreenButton="false"
                  :enableProtokolTypes="true"
                />
              </div>
            </div>
          </div>
        </vue-resizable>

        <!-- Full Width Editor (when no split) -->
        <div v-else class="vscode-editor-pane vscode-editor-full">
          <div class="vscode-editor-container">
            <div class="vscode-tabs">
              <div 
                v-for="(activeEditor, index) in activeEditors" 
                :key="activeEditor.id"
                :class="['vscode-tab', { 'vscode-tab-active': activeTab === index }]"
                @click="activeTab = index"
              >
                <img :src="iconRender(activeEditor.ext)" class="vscode-tab-icon" />
                <span class="vscode-tab-label">{{ activeEditor.name }}</span>
                <span class="vscode-tab-close" @click.stop="closeActiveEditor(activeEditor)">
                  <b-icon icon="x" font-scale="0.9"></b-icon>
                </span>
              </div>
            </div>
            <div class="vscode-editor-content">
              <div v-if="activeEditors.length > 0" class="vscode-editor-file">
                <div class="vscode-breadcrumb">
                  {{ generateNodePath(activeEditors[activeTab], activeEditors[activeTab].name) }}
                </div>
                <MonacoEditor 
                  :value="currentEditorValue(activeEditors[activeTab])"
                  @input="(e) => editorInput(activeEditors[activeTab], e)"
                  :language="getLanguageFromFile(activeEditors[activeTab].name)"
                  :options="monacoOptions"
                  class="vscode-monaco"
                  :showFullscreenButton="false"
                  :enableProtokolTypes="true"
                />
              </div>
              <div v-else class="vscode-empty-state">
                <div class="vscode-empty-icon">
                  <b-icon icon="file-earmark-code" font-scale="3"></b-icon>
                </div>
                <h4>No file selected</h4>
                <p>Select a file from the explorer to start editing</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Editor (Split View) -->
        <div v-if="activeEditorsRight.length" class="vscode-editor-pane vscode-editor-right">
          <div class="vscode-editor-container">
            <div class="vscode-tabs">
              <div 
                v-for="(activeEditorRight, index) in activeEditorsRight" 
                :key="activeEditorRight.id"
                :class="['vscode-tab', { 'vscode-tab-active': activeTabRight === index }]"
                @click="activeTabRight = index"
              >
                <img :src="iconRender(activeEditorRight.ext)" class="vscode-tab-icon" />
                <span class="vscode-tab-label">{{ activeEditorRight.name }}</span>
                <span class="vscode-tab-close" @click.stop="closeActiveEditorRight(activeEditorRight)">
                  <b-icon icon="x" font-scale="0.9"></b-icon>
                </span>
              </div>
            </div>
            <div class="vscode-editor-content">
              <div class="vscode-editor-file">
                <div class="vscode-breadcrumb">
                  {{ generateNodePath(activeEditorsRight[activeTabRight], activeEditorsRight[activeTabRight].name) }}
                </div>
                <MonacoEditor 
                  :value="currentEditorValue(activeEditorsRight[activeTabRight])"
                  @input="(e) => editorInput(activeEditorsRight[activeTabRight], e)"
                  :language="getLanguageFromFile(activeEditorsRight[activeTabRight].name)"
                  :options="monacoOptions"
                  class="vscode-monaco"
                  :showFullscreenButton="false"
                  :enableProtokolTypes="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Chat Panel -->
      <vue-resizable 
        v-if="showChat"
        :width="chatWidth" 
        :active="['l']"
        :maxWidth="600"
        :minWidth="300"
        @resize:move="onChatResize"
        class="vscode-chat-sidebar"
      >
        <div class="vscode-chat-panel">
          <div class="vscode-chat-header">
            <span class="vscode-sidebar-title">AI ASSISTANT</span>
            <b-icon 
              icon="x" 
              class="vscode-chat-close"
              @click="toggleChat"
            ></b-icon>
          </div>
          
          <div class="vscode-chat-messages" ref="chatMessages">
            <div class="vscode-chat-coming-soon">
              <b-icon icon="magic" font-scale="3" class="mb-3"></b-icon>
              <h5>Coming Soon</h5>
              <p class="text-muted">AI Assistant feature is under development</p>
            </div>
          </div>
        </div>
      </vue-resizable>
    </div>

    <!-- Status Bar -->
    <div class="vscode-statusbar">
      <div class="vscode-statusbar-left">
        <slot name="footer"></slot>
      </div>
      <div class="vscode-statusbar-right">
        <span v-if="activeEditors[activeTab]" class="vscode-statusbar-item">
          {{ getLanguageFromFile(activeEditors[activeTab].name).toUpperCase() }}
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// VS Code Color Palette
$vscode-bg: #1e1e1e;
$vscode-sidebar-bg: #252526;
$vscode-menu-bg: #323233;
$vscode-tab-bg: #2d2d2d;
$vscode-tab-active-bg: #1e1e1e;
$vscode-border: #3c3c3c;
$vscode-text: #cccccc;
$vscode-text-dim: #858585;
$vscode-accent: #007acc;
$vscode-hover: #2a2d2e;
$vscode-statusbar: #007acc;

body {
  background-color: $vscode-bg;
  overflow: hidden;
  position: static;
  margin: 0;
  padding: 0;
}

.vscode-editor {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: $vscode-bg;
  color: $vscode-text;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
  overflow: hidden;
}

// Menu Bar
.vscode-menubar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 35px;
  background: $vscode-menu-bg;
  border-bottom: 1px solid $vscode-border;
  padding: 0 12px;
  flex-shrink: 0;

  .vscode-menubar-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .vscode-menubar-center {
    flex: 1;
    text-align: center;
    
    .vscode-title {
      font-size: 13px;
      color: $vscode-text-dim;
    }
  }

  .vscode-menubar-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

// Chat Icon
.vscode-chat-icon {
  color: $vscode-text-dim;
  cursor: pointer;
  font-size: 18px;
  transition: color 0.1s ease;
  
  &:hover {
    color: $vscode-text;
  }
}

// AI Chat Sidebar
.vscode-chat-sidebar {
  height: 100%;
  border-left: 1px solid $vscode-border;
}

.vscode-chat-panel {
  height: 100%;
  background: $vscode-sidebar-bg;
  display: flex;
  flex-direction: column;
}

.vscode-chat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: $vscode-text;
  text-transform: uppercase;
  border-bottom: 1px solid $vscode-border;
  background: $vscode-sidebar-bg;
  
  .vscode-sidebar-title {
    flex: 1;
  }
}

.vscode-chat-close {
  cursor: pointer;
  color: $vscode-text-dim;
  font-size: 16px;
  transition: color 0.1s ease;
  
  &:hover {
    color: $vscode-text;
  }
}

.vscode-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #424242;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #4e4e4e;
  }
}

.vscode-chat-coming-soon {
  text-align: center;
  color: $vscode-text;
  
  h5 {
    color: $vscode-text;
    margin-bottom: 8px;
  }
  
  p {
    font-size: 13px;
  }
  
  .b-icon {
    color: $vscode-accent;
  }
}

.vscode-chat-input-container {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 12px 16px;
  background: $vscode-menu-bg;
  border-top: 1px solid $vscode-border;
}

.vscode-chat-input {
  flex: 1;
  background: $vscode-bg !important;
  border: 1px solid $vscode-border !important;
  color: $vscode-text !important;
  font-size: 13px;
  padding: 10px 12px;
  border-radius: 6px;
  resize: none;
  min-height: 42px;
  max-height: 200px;
  overflow-y: auto;
  font-family: inherit;
  line-height: 1.5;
  
  &:focus {
    outline: none;
    border-color: $vscode-accent !important;
  }
  
  &::placeholder {
    color: $vscode-text-dim;
  }
}

.vscode-chat-send {
  padding: 8px 12px !important;
  color: $vscode-accent !important;
  font-size: 18px;
  
  &:hover:not(:disabled) {
    color: lighten($vscode-accent, 10%) !important;
  }
  
  &:disabled {
    color: $vscode-text-dim !important;
    opacity: 0.5;
  }
}

.vscode-chat-stop {
  padding: 8px 12px !important;
  color: #f48771 !important;
  font-size: 18px;
  
  &:hover {
    color: lighten(#f48771, 10%) !important;
  }
}

// Container
.vscode-container {
  display: flex;
  flex: 1;
  overflow: hidden;
}

// Sidebar
.vscode-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: $vscode-sidebar-bg;
  border-right: 1px solid $vscode-border;
  overflow: hidden;

  .vscode-sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.5px;
    color: $vscode-text;
    text-transform: uppercase;
    border-bottom: 1px solid $vscode-border;
    background: $vscode-sidebar-bg;
  }

  .vscode-sidebar-actions {
    display: flex;
    gap: 8px;
  }

  .vscode-sidebar-icon {
    cursor: pointer;
    color: $vscode-text-dim;
    font-size: 16px;
    transition: color 0.1s ease;

    &:hover {
      color: $vscode-text;
    }
  }

  .vscode-sidebar-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 4px 0;

    &::-webkit-scrollbar {
      width: 10px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: #424242;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: #4e4e4e;
    }
  }
}

.vscode-tree-item {
  cursor: pointer;
  padding: 2px 0;
}

.vscode-tree-input {
  background: $vscode-bg !important;
  border: 1px solid $vscode-accent !important;
  color: $vscode-text !important;
  font-size: 13px;
  padding: 2px 4px;
  height: auto;
}

.vscode-file-icon {
  width: 16px;
  height: 16px;
  margin-right: 6px;
}

// Editor Area
.vscode-editor-area {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.vscode-editor-pane {
  display: flex;
  flex-direction: column;
  background: $vscode-bg;
  
  &.vscode-editor-full {
    flex: 1;
  }

  &.vscode-editor-right {
    border-left: 1px solid $vscode-border;
    flex: 1;
  }
}

.vscode-editor-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
}

// Tabs
.vscode-tabs {
  display: flex;
  background: $vscode-tab-bg;
  border-bottom: 1px solid $vscode-border;
  overflow-x: auto;
  overflow-y: hidden;
  flex-shrink: 0;

  &::-webkit-scrollbar {
    height: 0px;
  }
}

.vscode-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: $vscode-tab-bg;
  border-right: 1px solid $vscode-border;
  cursor: pointer;
  font-size: 13px;
  color: $vscode-text-dim;
  transition: all 0.1s ease;
  white-space: nowrap;
  min-width: max-content;

  &:hover {
    background: $vscode-hover;
    color: $vscode-text;

    .vscode-tab-close {
      opacity: 1;
    }
  }

  &.vscode-tab-active {
    background: $vscode-tab-active-bg;
    color: $vscode-text;
    border-bottom: 2px solid $vscode-accent;

    .vscode-tab-close {
      opacity: 1;
    }
  }
}

.vscode-tab-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.vscode-tab-label {
  flex-shrink: 0;
}

.vscode-tab-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  opacity: 0;
  transition: all 0.1s ease;
  margin-left: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &:active {
    background: rgba(255, 255, 255, 0.15);
  }
}

// Editor Content
.vscode-editor-content {
  flex: 1;
  overflow: hidden;
  background: $vscode-bg;
}

.vscode-editor-file {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.vscode-breadcrumb {
  padding: 6px 12px;
  font-size: 12px;
  color: $vscode-text-dim;
  background: $vscode-bg;
  border-bottom: 1px solid $vscode-border;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.vscode-monaco {
  flex: 1;
  height: 100% !important;
}

.vscode-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: $vscode-text-dim;

  .vscode-empty-icon {
    margin-bottom: 16px;
    opacity: 0.3;
  }

  h4 {
    font-size: 16px;
    font-weight: 400;
    margin-bottom: 8px;
    color: $vscode-text;
  }

  p {
    font-size: 13px;
    margin: 0;
  }
}

// Status Bar
.vscode-statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 22px;
  background: $vscode-statusbar;
  color: white;
  font-size: 12px;
  padding: 0 12px;
  flex-shrink: 0;

  .vscode-statusbar-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .vscode-statusbar-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .vscode-statusbar-item {
    padding: 0 8px;
    cursor: pointer;
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  }
}

// Context Menu
#vscode-context-menu {
  background: $vscode-menu-bg;
  border: 1px solid $vscode-border;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  display: block;
  list-style: none;
  margin: 0;
  padding: 4px 0;
  position: fixed;
  z-index: 10000;
  border-radius: 4px;
  font-size: 13px;
  min-width: 160px;

  li {
    margin: 0;
    padding: 6px 12px;
    color: $vscode-text;
    cursor: pointer;
    transition: all 0.1s ease;

    &:hover {
      background: $vscode-accent;
      color: white;
    }
  }
}

// Tree Overrides
::v-deep {
  .vtl {
    color: $vscode-text;
    font-size: 13px;
  }

  .vtl-node-main {
    padding: 3px 6px;
    border-radius: 3px;
    
    &:hover {
      background-color: $vscode-hover !important;
    }
  }

  .vtl-node-content {
    display: flex;
    align-items: center;
  }

  .vtl-operation {
    display: none;
  }

  .treeview {
    background-color: transparent;
  }
}

// Custom VS Code Tree
::v-deep .vscode-tree {
  width: 100%;
  user-select: none;
}

::v-deep .vscode-tree-node {
  width: 100%;
}

::v-deep .vscode-tree-node-content {
  display: flex;
  align-items: center;
  height: 22px;
  line-height: 22px;
  cursor: pointer;
  color: $vscode-text;
  font-size: 13px;
  transition: background-color 0.1s ease;

  &:hover {
    background-color: $vscode-hover;
  }
}

::v-deep .vscode-tree-node-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 22px;
  margin-right: 2px;
  flex-shrink: 0;
  color: $vscode-text-dim;
  vertical-align: top;
}

::v-deep .vscode-tree-node-spacer {
  width: 16px;
  height: 22px;
  display: inline-block;
  vertical-align: top;
}

::v-deep .vscode-tree-node-label {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  line-height: 22px;
}

::v-deep .vscode-tree-folder-icon {
  width: 16px;
  height: 16px;
  color: #dcb67a;
  flex-shrink: 0;
  vertical-align: middle;
}

::v-deep .vscode-tree-file-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  opacity: 0.8;
  vertical-align: middle;
}

::v-deep .vscode-tree-node-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

::v-deep .vscode-tree-input {
  background: $vscode-bg !important;
  border: 1px solid $vscode-accent !important;
  color: $vscode-text !important;
  font-size: 13px;
  padding: 0 4px;
  height: 18px;
  line-height: 18px;
  outline: none;
  flex: 1;
  min-width: 0;
}

::v-deep .vscode-tree-node-children {
  width: 100%;
}


// Resizable Handles
.handle {
  display: none !important;
}

.handle-mr {
  position: fixed;
  display: block !important;
  height: 100%;
  top: 0;
  width: 4px;
  background: transparent;
  opacity: 1;
  z-index: 999;
  cursor: col-resize;
  transition: background 0.1s ease;

  &:hover {
    background: $vscode-accent;
  }
}

.vdr {
  height: 100% !important;
  border: none;
  overflow: hidden;
}
</style>

<script>
import { Header, Sidebar, SidebarNav, Footer } from "@/components/ui/vue/src";
import _ from "lodash";
import MonacoEditor from "@/components/form/Editor/MonacoEditor.vue";

import { VueTreeList, Tree, TreeNode as VueTreeNode } from "vue-tree-list";
import VueResizable from "vue-resizable";
import Vue from "vue";

// icons
import vueicon from "@/assets/editor/vuelogo.png";
import jsicon from "@/assets/editor/jsicon.png";
import cssicon from "@/assets/editor/cssicon.png";
import svgicon from "@/assets/editor/svgicon.png";
import jsonIcon from "@/assets/editor/jsonicon.png";

// Custom Tree Node Component
const CustomTreeNode = {
  name: 'TreeNode',
  props: {
    node: Object,
    level: Number,
    editingId: [String, Number],
  },
  data() {
    return {
      isExpanded: false,
    };
  },
  computed: {
    hasChildren() {
      return !this.node.isLeaf && this.node.children && this.node.children.length > 0;
    },
    icon() {
      if (this.node.isLeaf) {
        const ext = this.node.ext || this.resolveFileExt(this.node.name);
        return this.iconRender(ext);
      }
      return null;
    },
    indentStyle() {
      return {
        paddingLeft: `${this.level * 12 + 8}px`,
      };
    },
    isEditing() {
      return this.editingId === this.node.id;
    },
  },
  methods: {
    toggle() {
      if (this.hasChildren) {
        this.isExpanded = !this.isExpanded;
        this.$emit('toggle', this.node);
      }
    },
    onClick(e) {
      if (this.node.isLeaf) {
        this.$emit('click', this.node);
      } else {
        this.toggle();
      }
    },
    onContextMenu(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$emit('contextmenu', e, this.node);
    },
    onRename(e) {
      if (e.key === 'Enter') {
        this.$emit('rename', this.node);
      }
    },
    resolveFileExt(name) {
      let parts = name.split(".");
      return parts[parts.length - 1];
    },
    iconRender(ext) {
      let icons = {
        vue: vueicon,
        js: jsicon,
        css: cssicon,
        svg: svgicon,
        json: jsonIcon,
      };
      return _.get(icons, ext, "");
    },
  },
  template: `
    <div class="vscode-tree-node">
      <div 
        class="vscode-tree-node-content"
        :style="indentStyle"
        @click="onClick"
        @contextmenu="onContextMenu"
      >
        <div class="vscode-tree-node-icon" @click.stop="toggle">
          <b-icon 
            v-if="!node.isLeaf" 
            :icon="isExpanded ? 'chevron-down' : 'chevron-right'"
            font-scale="0.8"
            style="width: 16px; height: 16px;"
          ></b-icon>
          <span v-else class="vscode-tree-node-spacer"></span>
        </div>
        
        <div class="vscode-tree-node-label">
          <b-icon 
            v-if="!node.isLeaf" 
            :icon="isExpanded ? 'folder2-open' : 'folder2'"
            class="vscode-tree-folder-icon"
            style="width: 16px; height: 16px; min-width: 16px;"
          ></b-icon>
          <img v-else-if="icon" :src="icon" class="vscode-tree-file-icon" style="width: 16px; height: 16px; min-width: 16px; object-fit: contain;" />
          <b-icon v-else icon="file-earmark" class="vscode-tree-file-icon" style="width: 16px; height: 16px; min-width: 16px;"></b-icon>
          
          <input
            v-if="isEditing"
            v-model="node.name"
            @keyup.enter="onRename"
            @blur="onRename"
            ref="renameInput"
            class="vscode-tree-input"
            @click.stop
          />
          <span v-else class="vscode-tree-node-name">{{ node.name }}</span>
        </div>
      </div>
      
      <div v-if="isExpanded && hasChildren" class="vscode-tree-node-children">
        <tree-node
          v-for="child in node.children"
          :key="child.id"
          :node="child"
          :level="level + 1"
          :editing-id="editingId"
          @click="$emit('click', $event)"
          @toggle="$emit('toggle', $event)"
          @contextmenu="$emit('contextmenu', $event, arguments[1])"
          @rename="$emit('rename', $event)"
        />
      </div>
    </div>
  `,
  mounted() {
    // Auto-expand root level
    if (this.level === 0) {
      this.isExpanded = true;
    }
    
    // Focus input when editing
    if (this.isEditing && this.$refs.renameInput) {
      this.$nextTick(() => {
        this.$refs.renameInput.focus();
        this.$refs.renameInput.select();
      });
    }
  },
  watch: {
    isEditing(val) {
      if (val && this.$refs.renameInput) {
        this.$nextTick(() => {
          this.$refs.renameInput.focus();
          this.$refs.renameInput.select();
        });
      }
    },
  },
};

export default {
  name: "TemplateEditor",
  props: {
    value: {
      type: Array,
    },

    saving: {
      type: Boolean,
      default: false,
    },

    builderConnected: {
      type: Boolean,
      default: false,
    },

    builderPort: {
      type: Number,
      default: 11403,
    },

    showBuilderSettings: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    MonacoEditor,
    Header,
    Footer,
    Sidebar,
    SidebarNav,
    VueTreeList,
    Tree,
    TreeNode: CustomTreeNode,
    VueResizable,
  },
  data: function () {
    return {
      handlers: ["r"],
      templateReady: false,
      expressionError: "",
      activeEditors: [],
      activeEditorsRight: [],
      rightEditorWidth: 0,
      leftEditorWidth: "auto",
      pages: [],
      newTree: {},
      data: null,
      viewMenu: false,
      top: "0px",
      left: "0px",
      currentMenuNode: null,
      nodeEditable: null,
      sidebarWidth: 250,
      chatWidth: 400,
      modules: {},
      appName: '',
      activeTab: null,
      activeTabRight: null,
      editorState: {
        activeEditors: [],
      },
      showChat: false,
      chatInput: '',
      chatMessages: [],
      chatLoading: false,
      chatAbortController: null,
    };
  },
  watch: {
    activeTabRight() {
      // Monaco handles focus automatically
    },
    activeTab() {
      // Monaco handles focus automatically
    },
  },
  computed: {
    nodes: {
      get() {
        return new Tree(this.value)
      },

      set(val) {
        this.$emit('input', val.children)
      }
    },
    monacoOptions() {
      return {
        theme: 'vs-dark',
        automaticLayout: true,
        minimap: { enabled: true },
        scrollBeyondLastLine: false,
      };
    },
    localShowBuilderSettings: {
      get() {
        return this.showBuilderSettings;
      },
      set(val) {
        this.$emit('update:showBuilderSettings', val);
      }
    },
    localBuilderPort: {
      get() {
        return this.builderPort;
      },
      set(val) {
        this.$emit('update:builderPort', val);
      }
    },
  },
  methods: {
    async onDel(node) {
      let resp = await this.$mainDialog.msgBoxConfirm(
        "Are you sure you want to delete " + node.name + "?",
        {
          headerBgVariant: "dark",
          bodyBgVariant: "dark",
          footerBgVariant: "dark",
        }
      );
      if (!resp) return;

      node.remove();

      this.$emit('input', this.nodes.children)
    },

    onChangeName(node) {
      if (node.node) {
        // set ext of the file
        node.node.ext = this.resolveFileExt(node.newName);
        if (node.node.editorOptions)
          node.node.editorOptions.mode = this.resolveEditorMode(node.node.ext);
        // find active editor and change mode of editor depending on ext
        let activeEditor = this.findNodeByID(node.id, this.activeEditors);
        if (activeEditor) {
          activeEditor = node.node;
        }
      }

      this.$emit('input', this.nodes.children)
    },

    resolveFileExt(name) {
      let parts = name.split(".");
      return parts[parts.length - 1];
    },

    getLanguageFromFile(filename) {
      const ext = this.resolveFileExt(filename);
      switch (ext) {
        case "vue":
          return "html"; // Vue SFC uses HTML-like syntax
        case "js":
          return "javascript";
        case "ts":
          return "typescript";
        case "css":
          return "css";
        case "svg":
        case "xml":
          return "xml";
        case "json":
          return "json";
        case "html":
          return "html";
        default:
          return "plaintext";
      }
    },

    resolveEditorMode(ext) {
      switch (ext) {
        case "vue":
          return "text/x-vue";
        case "js":
          return { name: "javascript", globalVars: true };
        case "css":
          return "text/x-less";
        case "svg":
          return "application/xml";
        case "json":
          return "application/json";
        default:
          return "text/plain";
      }
    },

    onClick(params) {
      if (!params.isLeaf) return;

      params.editorOptions = {
        mode: this.resolveEditorMode(this.resolveFileExt(params.name)),
      };

      let found = this.activeEditors.findIndex((e) => {
        return e.id == params.id;
      });

      if (found < 0) {
        this.activeEditors.push(params);
      } else {
        this.activeTab = found;
      }

      console.log("activeEditors", this.activeEditors)

      this.saveEditorState();
    },

    onNodeClick(node) {
      this.onClick(node);
    },

    onNodeToggle(node) {
      // Toggle is handled by the TreeNode component itself
    },

    handleRename(node) {
      this.nodeEditable = null;
      this.onChangeName({
        newName: node.name,
        node: node,
      });
    },

    saveEditorState() {
      this.editorState.activeEditors = this.activeEditors.map((e) => {
        return e.path;
      });

      localStorage.setItem(
        `app_editor_${this.$route.params.id}`,
        JSON.stringify(this.editorState)
      );
    },

    loadEditorState() {
      try {
        let state = JSON.parse(
          localStorage.getItem(`app_editor_${this.$route.params.id}`)
        );

        if (state && state.activeEditors) {
          state.activeEditors.forEach((a) => {
            if (!a || !a.id) return; // Skip null/invalid entries
            
            let node = this.findNodeByID(a.id);
            if (node) {
              this.onClick(node);
              return;
            }

            // remove node from state
            state.activeEditors = state.activeEditors.filter((e) => {
              return e && e != a;
            });
          });

          localStorage.setItem(
            `app_editor_${this.$route.params.id}`,
            JSON.stringify(state)
          );
        }
      } catch (error) {
        console.error('Error loading editor state:', error);
      }
    },

    addNode(node) {
      if (!node.isLeaf) return;

      node.editorOptions = {
        mode: this.resolveEditorMode(this.resolveFileExt(node.name)),
      };
    },

    generateNodePath(n, p) {
      let path = p

      if (n && n.parent) {
        path = n.parent.name + "/" + path

        return this.generateNodePath(n.parent, path)
      }
      
      if (n.name == "root") {
        const appRoot = n.children[0].name

        return _.trimStart(path.replaceAll(`root/${appRoot}`, ""), "/")
      }

      return _.trimStart(path, "/")
    },

    editorOptions(override) {
      return {
        tabSize: 2,
        line: true,
        autoRefresh: true,
        lineNumbers: true,
        foldGutter: true,
        firstLineNumber: 1,
        styleSelectedText: true,
        matchBrackets: true,
        spellcheck: true,
        highlightSelectionMatches: { showToken: /\w/ },
        autoCloseBrackets: true,
        extraKeys: {
          "Ctrl-Space": "autocomplete",
          "Ctrl-/": "toggleComment",
        },
        showCursorWhenSelecting: true,
        theme: "material-palenight",
        scrollbarStyle: "overlay",
        ...override,
      };
    },

    editorInput: _.debounce(function (activeEditor, val) {
      let node = this.findNodeByID(activeEditor.id);
      node.content = val;

      this.$emit("input", this.nodes.children)
    }, 500),

    currentEditorValue(activeEditor) {
      return activeEditor.content || "";
    },

    findNodeByID(id, nodes) {
      if (!nodes) {
        nodes = this.nodes.children;
      }

      let node = null;
      for (let i = 0; i <= nodes.length; i++) {
        if (!nodes[i]) continue;

        if (nodes[i].id == id) {
          node = nodes[i];
          break;
        }

        if (!node) node = this.findNodeByID(id, nodes[i].children || []);
      }

      return node;
    },

    findNodeByPath(path, nodes) {
      if (!nodes) {
        nodes = this.nodes.children;
      }

      let node = null;
      for (let i = 0; i <= nodes.length; i++) {
        if (!nodes[i]) continue;

        const generatedPath = this.generateNodePath(nodes[i], nodes[i].name)
        if (generatedPath == path) {
          node = nodes[i];
          break;
        }

        if (!node) node = this.findNodeByPath(path, nodes[i].children || []);
      }

      return node;
    },

    closeActiveEditor(editor) {
      const index = this.activeEditors.findIndex(e => e.id === editor.id);
      if (index === -1) return;

      this.activeEditors = this.activeEditors.filter(e => e.id !== editor.id);

      // Adjust activeTab if needed
      if (this.activeEditors.length === 0) {
        this.activeTab = null;
      } else if (this.activeTab >= this.activeEditors.length) {
        this.activeTab = this.activeEditors.length - 1;
      } else if (this.activeTab > index) {
        this.activeTab--;
      }

      this.saveEditorState();
    },

    closeActiveEditorRight(editor) {
      const index = this.activeEditorsRight.findIndex(e => e.id === editor.id);
      if (index === -1) return;

      this.activeEditorsRight = this.activeEditorsRight.filter(e => e.id !== editor.id);

      // Adjust activeTabRight if needed
      if (this.activeEditorsRight.length === 0) {
        this.activeTabRight = null;
      } else if (this.activeTabRight >= this.activeEditorsRight.length) {
        this.activeTabRight = this.activeEditorsRight.length - 1;
      } else if (this.activeTabRight > index) {
        this.activeTabRight--;
      }

      this.saveEditorState();
    },

    setMenu(top, left) {
      let largestHeight =
        window.innerHeight - this.$refs.right.offsetHeight - 25;
      let largestWidth = window.innerWidth - this.$refs.right.offsetWidth - 25;

      if (top > largestHeight) top = largestHeight;

      if (left > largestWidth) left = largestWidth;

      this.top = top + "px";
      this.left = left + "px";
    },

    closeMenu() {
      this.viewMenu = false;
    },

    openMenu(e, node) {
      console.log('openMenu called', e, node);
      e.preventDefault();
      e.stopPropagation();

      this.currentMenuNode = node;

      const actions = this.nodeActions();
      console.log('Available actions:', actions);

      if (actions.length < 1) {
        console.log('No actions available for this node');
        return;
      }

      this.viewMenu = true;
      console.log('viewMenu set to true');

      Vue.nextTick(
        function () {
          if (this.$refs.right) {
            this.$refs.right.focus();
            this.setMenu(e.clientY, e.clientX);
            console.log('Menu positioned at', e.clientY, e.clientX);
          } else {
            console.error('Menu ref not found');
          }
        }.bind(this)
      );
    },

    triggerNodeAction(action) {
      switch (action) {
        case "Delete":
          this.removeNode(this.currentMenuNode);
          break;
        case "Add file":
          this.addFile(this.currentMenuNode);
          break;
        case "Add folder":
          this.addFolder(this.currentMenuNode);
          break;
        case "Rename":
          this.renameNode(this.currentMenuNode);
          break;
        case "Open in Split View":
          this.OpenInSplitView(this.currentMenuNode);
          break;
      }

      this.closeMenu();
    },

    renameNode(node) {
      this.currentMenuNode = node;
      this.nodeEditable = node.id;
      Vue.nextTick(
        function () {
          if (this.$refs.renameInput) {
            if (Array.isArray(this.$refs.renameInput)) {
              this.$refs.renameInput[0].focus();
              this.$refs.renameInput[0].select();
            } else {
              this.$refs.renameInput.focus();
              this.$refs.renameInput.select();
            }
          }
        }.bind(this)
      );
    },

    OpenInSplitView(node) {
      node.editorOptions = {
        mode: this.resolveEditorMode(this.resolveFileExt(node.name)),
      };

      let found = this.activeEditorsRight.findIndex((e) => {
        return e.id == node.id;
      });

      if (found < 0) {
        this.activeEditorsRight.push(node);
      } else {
        this.activeTabRight = found;
      }
    },

    removeNode(node) {
      this.$mainDialog
        .msgBoxConfirm(`Are you sure you want to delete "${node.name}"?`, {
          headerBgVariant: "dark",
          footerBgVariant: "dark",
          bodyBgVariant: "dark",
        })
        .then((resp) => {
          if (resp) {
            node.remove();
            this.$emit('input', this.nodes.children);
          }
        });
    },

    addFile(node) {
      const newFile = new VueTreeNode({
        name: "new file",
        isLeaf: true,
        dragDisabled: false,
        addTreeNodeDisabled: false,
        addLeafNodeDisabled: false,
        delNodeDisabled: false,
        editNodeDisabled: false,
        children: null,
        content: '',
        ext: '',
        editorOptions: {
          mode: "text/plain",
        },
      });
      
      node.addChildren(newFile);
      
      // Force re-render by updating the value
      this.$emit('input', this.nodes.children);
      
      // Auto-rename the new file
      this.$nextTick(() => {
        this.renameNode(newFile);
      });
    },

    addFolder(node) {
      const newFolder = new VueTreeNode({
        name: "new folder",
        isLeaf: false,
        dragDisabled: false,
        addTreeNodeDisabled: false,
        addLeafNodeDisabled: false,
        delNodeDisabled: false,
        editNodeDisabled: false,
        children: [],
      });
      
      node.addChildren(newFolder);
      
      // Force re-render by updating the value
      this.$emit('input', this.nodes.children);
      
      // Auto-rename the new folder
      this.$nextTick(() => {
        this.renameNode(newFolder);
      });
    },

    addFileToRoot() {
      console.log('addFileToRoot called', this.nodes);
      // Find templates directory
      const rootFolder = this.nodes.children && this.nodes.children.length > 0 ? this.nodes.children[0] : null;
      if (rootFolder) {
        // Look for templates folder
        const templatesFolder = rootFolder.children && rootFolder.children.find(child => child.name === 'templates');
        if (templatesFolder) {
          this.addFile(templatesFolder);
        } else {
          // If no templates folder, add to root
          this.addFile(rootFolder);
        }
      } else {
        console.error('No root folder found');
      }
    },

    addFolderToRoot() {
      console.log('addFolderToRoot called', this.nodes);
      // Find templates directory
      const rootFolder = this.nodes.children && this.nodes.children.length > 0 ? this.nodes.children[0] : null;
      if (rootFolder) {
        // Look for templates folder
        const templatesFolder = rootFolder.children && rootFolder.children.find(child => child.name === 'templates');
        if (templatesFolder) {
          this.addFolder(templatesFolder);
        } else {
          // If no templates folder, add to root
          this.addFolder(rootFolder);
        }
      } else {
        console.error('No root folder found');
      }
    },

    nodeActions() {
      let actions = [];
      let node = this.currentMenuNode;
      
      // For folders, allow adding files and folders
      if (!node.isLeaf) {
        actions.push("Add file");
        actions.push("Add folder");
      }

      // Don't allow deleting the root node
      if (node.name !== 'root' && node.parent && node.parent.name !== 'root') {
        actions.push("Delete");
      }

      // Always allow rename except for root
      if (node.name !== 'root') {
        actions.push("Rename");
      }
      
      // Files can be opened in split view
      if (node.isLeaf) {
        actions.push("Open in Split View");
      }

      return actions;
    },

    nodeNameChange() {
      this.currentMenuNode.changeName(this.currentMenuNode.name);
      this.nodeEditable = null;
      this.onChangeName({
        newName: this.currentMenuNode.name,
        node: this.currentMenuNode,
      });
    },

    onEditorResize(data) {
      this.leftEditorWidth = data.width;
      this.$refs.editorPanelRight.style.width =
        window.innerWidth - this.sidebarWidth - data.width + "px";
    },

    onSidebarResize(data) {
      this.sidebarWidth = data.width;
    },

    onChatResize(data) {
      this.chatWidth = data.width;
    },

    iconRender(ext) {
      let icons = {
        vue: vueicon,
        js: jsicon,
        css: cssicon,
        svg: svgicon,
        json: jsonIcon,
      };

      return _.get(icons, ext, "");
    },

    toggleChat() {
      console.log('toggleChat called, current showChat:', this.showChat);
      this.showChat = !this.showChat;
      console.log('showChat is now:', this.showChat);
      if (this.showChat) {
        this.$nextTick(() => {
          if (this.$refs.chatInputField) {
            this.$refs.chatInputField.focus();
          }
        });
      }
    },

    async sendChatMessage() {
      if (!this.chatInput.trim() || this.chatLoading) return;

      const userMessage = {
        role: 'user',
        content: this.chatInput.trim(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      };

      this.chatMessages.push(userMessage);
      const userQuery = this.chatInput;
      this.chatInput = '';

      // Reset textarea height
      this.$nextTick(() => {
        const textarea = this.$refs.chatInputField;
        if (textarea) {
          textarea.style.height = 'auto';
        }
      });

      // Scroll to bottom
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
        }
      });

      this.chatLoading = true;
      this.chatAbortController = new AbortController();

      try {
        // TODO: Replace with actual AI API call
        // For now, simulate a response that can be aborted
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(resolve, 1500);
          this.chatAbortController.signal.addEventListener('abort', () => {
            clearTimeout(timeout);
            reject(new Error('Request aborted'));
          });
        });
        
        const assistantMessage = {
          role: 'assistant',
          content: `I received your message: "${userQuery}". This is a placeholder response. Connect this to your AI API to get real responses.`,
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        };

        this.chatMessages.push(assistantMessage);
      } catch (error) {
        if (error.message === 'Request aborted') {
          console.log('Chat request aborted by user');
          this.chatMessages.push({
            role: 'assistant',
            content: 'Request stopped.',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          });
        } else {
          console.error('Error sending chat message:', error);
          this.chatMessages.push({
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          });
        }
      } finally {
        this.chatLoading = false;
        
        // Scroll to bottom after response
        this.$nextTick(() => {
          if (this.$refs.chatMessages) {
            this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
          }
        });
      }
    },

    stopChatMessage() {
      if (this.chatAbortController) {
        this.chatAbortController.abort();
        this.chatAbortController = null;
      }
    },

    adjustTextareaHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.chatInputField;
        if (!textarea) return;

        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        
        // Set height based on scrollHeight, capped at max-height (200px)
        const newHeight = Math.min(textarea.scrollHeight, 200);
        textarea.style.height = newHeight + 'px';
      });
    },
  },

  mounted() {
    console.log('TemplateEditor mounted');
    console.log('Initial showChat value:', this.showChat);
    
    document.onkeyup = function (e) {
      this.$emit('keyup', e)
    }.bind(this);

    this.loadEditorState();
    console.log('Initialization complete');
  },
};
</script>
